/**
 * AdminLoginButton — discreet "Staff / Admin login" floating button that
 * any public-facing page (EventLanding, school slug page, church public
 * page, etc.) can drop in. Opens a modal that authenticates against the
 * /api/admin/login endpoint, then routes to the user's smart landing
 * (auth_landing.py) — so an event team member is sent straight to
 * /admin/event/{eid}, a school admin to /admin/schools/{tid}, etc.
 *
 * Usage:
 *   <AdminLoginButton context={{ eventId, tenantId }} />
 *
 * Context is forwarded as `?redirect=...` so smart-landing knows where
 * to push them if their role doesn't have a hard-coded landing.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LogIn, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import afterLogin from '@/lib/afterLogin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLoginButton({
  context = {},
  variant = 'floating',     // 'floating' | 'inline'
  label = 'Staff / Admin login',
  className = '',
}) {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Invalid credentials');
      }
      const data = await res.json();
      // Persist auth — the store expects individual parameters: (token, user, userType, eventId, permissions)
      const token = data.access_token || data.token;
      const user = data.user || data.admin || {};
      setAuth(token, user, 'admin', context.eventId || null, user.permissions || []);
      toast.success(`Welcome, ${data.user?.name || data.user?.email || 'team member'}`);
      // Build a redirect hint from the page context so smart-landing has a
      // sensible default if the role doesn't map cleanly.
      let redirect;
      if (context.eventId) redirect = `/admin/event/${context.eventId}`;
      else if (context.tenantId) redirect = `/admin/schools/${context.tenantId}`;
      else if (context.churchId) redirect = `/admin/church-hierarchy`;
      await afterLogin({
        navigate, userType: 'admin',
        tenantId: context.tenantId, redirect,
      });
      setOpen(false);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const trigger = variant === 'floating' ? (
    <Button
      onClick={() => setOpen(true)}
      variant="outline"
      size="sm"
      data-testid="admin-login-floating-btn"
      className={`fixed bottom-4 right-4 z-40 shadow-lg bg-white/95 backdrop-blur ${className}`}
    >
      <Lock className="h-3.5 w-3.5 mr-1.5" /> {label}
    </Button>
  ) : (
    <Button
      onClick={() => setOpen(true)}
      variant="ghost"
      size="sm"
      data-testid="admin-login-inline-btn"
      className={className}
    >
      <Lock className="h-3.5 w-3.5 mr-1.5" /> {label}
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LogIn className="h-4 w-4 text-indigo-600" /> Staff / Admin login</DialogTitle>
            <DialogDescription>
              Sign in to access your event, school, church, or department workspace.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="admin-login-email"
                required
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="admin-login-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Sign in
            </Button>
            <p className="text-xs text-slate-500 text-center">
              You'll be routed to your assigned workspace automatically.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
